import styles from "./OpenListQuestion.module.css";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

export default function OpenListQuestion({ answerType = "str", value, onChange, details = {} }) {
    const isInt = answerType === "int";
    const { t } = useTranslation();
    console.log("OpenListQuestion render:", { answerType, value, details });
    const minVal = Number.isFinite(details.min) ? details.min : 0;
    const maxVal = Number.isFinite(details.max) ? details.max : undefined;

    // "extras" options (checkboxes under the list) – pozostają bez zmian
    const options = details.options || [];
    const textFields = details.textFields || [];
    const hasExtras = options.length > 0;

    // Nowa funkcja: lista rozwijalna dla każdego pola na liście (zamiast input tekstowego)
    // Nie korzystamy z details.options; czytamy osobne źródło
    const rawSelect = details.selectOptions || [];
    const selectOptions = Array.isArray(rawSelect)
        ? rawSelect.map((opt) =>
            (opt && typeof opt === 'object')
                ? { value: opt.value, label: opt.label ?? String(opt.value ?? "") }
                : { value: String(opt ?? ""), label: String(opt ?? "") }
        )
        : [];
    const useDropdown = selectOptions.length > 0;

    // Obsługa zgodności wstecznej: część starszych miejsc mogła przekazywać { items: [...] }
    const initialList = Array.isArray(value?.choice) && value.choice.length > 0
        ? value.choice
        : (Array.isArray(value?.items) && value.items.length > 0 ? value.items : []);
    const choice = initialList.length > 0 ? initialList : [""];

    const d = (value?.details && typeof value.details === "object") ? value.details : {};
    const selected = Array.isArray(d.selected) ? d.selected : [];
    const texts = (d.texts && typeof d.texts === "object") ? d.texts : {};

    const anyChecked = selected.length > 0;

    const emit = (next) => {
        const payload = {
            choice: next.choice ?? choice,
            details: next.details ?? { selected, texts },
        };
        onChange?.(payload);
    };

    // Gdy lista rozwijalna pojawia się (np. po załadowaniu danych ankiety),
    // zainicjalizuj wartość w store odpowiedzi, aby uniknąć problemów z pierwszym renderem.
    useEffect(() => {
        if (!useDropdown) return;
        const cur = Array.isArray(value?.choice)
            ? value.choice
            : (Array.isArray(value?.items) ? value.items : []);
        // jeśli brak wartości lub są puste/null, ustaw domyślne [""] aby zapewnić kontrolowany komponent
        const needsInit = !Array.isArray(cur) || cur.length === 0 || cur.some((v) => v == null);
        if (needsInit) {
            emit({ choice: cur && cur.length ? cur.map((v) => (v == null ? "" : v)) : [""] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useDropdown]);

    const handleItemChange = (idx, raw) => {
        const nextItems = [...choice];
        // Dla input number: konwersja do number; dla select zwykle przechowujemy string etykiety.
        // Jeśli answerType=int i uda się sparsować liczbę – zapisujemy liczbę; w przeciwnym razie string.
        let nextVal = raw;
        if (isInt) {
            if (raw === "") {
                nextVal = "";
            } else {
                const n = Number(raw);
                nextVal = Number.isNaN(n) ? raw : n;
            }
        }
        nextItems[idx] = nextVal;
        emit({ choice: nextItems });
    };

    const getMaxSelectableCount = () => {
        if (!useDropdown) return Infinity;
        return selectOptions.filter((opt) => String(opt.value ?? "") !== "").length;
    };

    const addItem = () => {
        const maxCount = getMaxSelectableCount();
        if (useDropdown && choice.length >= maxCount) return;
        emit({ choice: [...choice, ""] });
    };

    const removeLast = () => {
        if (choice.length > 1) {
            emit({ choice: choice.slice(0, -1) });
        }
    };

    const toggleOption = (opt) => {
        // Single-select behavior: selecting a new option replaces previous selection.
        // Clicking the same option again will deselect it (resulting in none selected).
        let nextSelected;
        if (selected.includes(opt)) {
            nextSelected = [];
        } else {
            nextSelected = [opt];
        }
        emit({ details: { selected: nextSelected, texts: { ...texts } } });
    };

    const handleExtraTextChange = (opt, val) => {
        emit({ details: { selected: [...selected], texts: { ...texts, [opt]: val } } });
    };

    // Pomocniczo: zbuduj zbiór już wybranych wartości (poza pustymi), porównujemy jako stringi
    const selectedValueAt = (i) => String(choice?.[i] ?? "");
    const selectedSetExcluding = (excludeIdx) => {
        const s = new Set();
        (choice || []).forEach((v, i) => {
            if (i === excludeIdx) return;
            const sv = String(v ?? "");
            if (sv !== "") s.add(sv);
        });
        return s;
    };
    const availableOptionsForIndex = (idx) => {
        if (!useDropdown) return [];
        const taken = selectedSetExcluding(idx);
        const own = selectedValueAt(idx);
        return selectOptions.filter((opt) => {
            const val = String(opt.value ?? "");
            if (val === own) return true; // zawsze pozwól na aktualnie wybraną wartość
            if (val === "") return true; // pozwól na pusty wybór (placeholder)
            return !taken.has(val);
        });
    };
    const remainingSelectableCount = useDropdown
        ? selectOptions.filter((opt) => {
            const val = String(opt.value ?? "");
            if (val === "") return false;
            // jeśli ktokolwiek ma tę wartość, nie liczymy jej jako wolnej
            return !(choice || []).some((v) => String(v ?? "") === val);
        }).length
        : Infinity;
    const maxSelectableCount = getMaxSelectableCount();

    return (
        <div className={styles.wrap}>


            <div className={styles.itemsWrap}>
                {choice.map((val, idx) => {
                    if (useDropdown) {
                        const current = val ?? "";
                        const avail = availableOptionsForIndex(idx);
                        return (
                            <select
                                key={idx}
                                className={styles.input}
                                value={String(current)}
                                onChange={(e) => handleItemChange(idx, e.target.value)}
                                disabled={anyChecked}
                                aria-label={t("selectText.placeholder", { defaultValue: "Wybierz..." })}
                            >
                                <option value="" disabled>{t("selectText.placeholder", { defaultValue: "Wybierz..." })}</option>
                                {avail.map((opt, i) => (
                                    <option key={i} value={String(opt.value)}>{opt.label}</option>
                                ))}
                            </select>
                        );
                    }
                    return (
                        <input
                            key={idx}
                            className={styles.input}
                            type={isInt ? "number" : "text"}
                            inputMode={isInt ? "numeric" : "text"}
                            pattern={isInt ? "[0-9]*" : undefined}
                            min={isInt ? minVal : undefined}
                            max={isInt && Number.isFinite(maxVal) ? maxVal : undefined}
                            value={val ?? ""}
                            onKeyDown={(e) => {
                                if (isInt && ["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                            }}
                            onChange={(e) => handleItemChange(idx, e.target.value)}
                            disabled={anyChecked}
                            placeholder={isInt ? t("placeholder.numberField") : t("placeholder.textField")}
                        />
                    );
                })}
            </div>

            <div className={styles.controls}>
                <button type="button" className={styles.btn} onClick={addItem} disabled={anyChecked || (useDropdown && (remainingSelectableCount <= 0 || choice.length >= maxSelectableCount))}>
                    {t("actions.addField", { defaultValue: "Dodaj pole" })}
                </button>
                <button type="button" className={styles.btn} onClick={removeLast} disabled={choice.length <= 1 || anyChecked}>
                    {t("actions.removeLast", { defaultValue: "Usuń ostatnie" })}
                </button>
            </div>
            {hasExtras && (
                <div className={styles.extraWrap}>
                    {options.map((opt, i) => {
                        const optLabel = typeof opt === 'object' && opt !== null ? opt.label : opt;
                        return (
                        <div key={i + 1} className={styles.optionRow}>
                            <label className={styles.optionLabel}>
                                <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    checked={selected.includes(i + 1)}
                                    onChange={() => toggleOption(i + 1)}
                                />
                                <span>{optLabel}</span>
                            </label>
                            {textFields.includes(i + 1) && (
                                <input
                                    type="text"
                                    className={styles.optionText}
                                    value={texts[i + 1] || ""}
                                    onChange={(e) => handleExtraTextChange(i + 1, e.target.value)}
                                    disabled={!selected.includes(i + 1)}
                                    placeholder={selected.includes(i + 1) ? t("placeholder.textField") : ""}
                                    aria-label={`${optLabel} details`}
                                />
                            )}
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
